import React, { useEffect, useRef } from "react";
import Layout from "../../components/admin/Layout";
import Helmet from "react-helmet";
import Table from "../../components/ui/Table";
import usePaginate from "../../hooks/api/usePaginate";
import apiRequest from "../../utils/admin/apiRequest";
import useModal from "../../hooks/ui/useModal";
import Pagination from "../../components/ui/Pagination";
import { FaEdit, FaPlus, FaTrashAlt } from "react-icons/fa";
import CreateCorporateCode from "../../components/admin/corporateCode/CreateCorporateCode";
import EditCorporateCode from "../../components/admin/corporateCode/EditCorporateCode";
import DeleteCorporateCode from "../../components/admin/corporateCode/DeleteCorporateCode";
import Modal from "../../components/ui/utils/Modal";
import Button from "../../components/ui/utils/Button";
import useFormInput from "../../hooks/form/useFormInput";
import InputField from "../../components/ui/form/InputField";
import { useSelector } from "react-redux";

const CorporateCode = () => {
  const {
    currentPage,
    data,
    error,
    fetchData,
    limit,
    loading,
    setPage,
    setSorting,
    sort,
    totalItems,
    totalPages,
    setFilters,
  } = usePaginate({
    apiMethod: apiRequest.getCorporateCode,
    dataKey: "corporates",
  });

  const { isOpen, openModal, closeModal, actionType } = useModal();

  const handleSort = (field) => {
    const order =
      sort.sortField === field && sort.sortOrder === "asc" ? "desc" : "asc";
    setSorting({ sortField: field, sortOrder: order });
  };

  const searchInitialValues = { corporateCode: "", corporateName: "" };
  const { values, handleChange, setValues } =
    useFormInput(searchInitialValues);

  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      const filters = {};
      if (values.corporateCode) {
        filters.corporateCode = {
          $regex: values.corporateCode,
          $options: "i",
        };
      }
      if (values.corporateName) {
        filters.corporateName = {
          $regex: values.corporateName,
          $options: "i",
        };
      }
      setFilters(filters);
    }, 500);

    return () => clearTimeout(debounceRef.current);
  }, [values]);

  const BRAND_NAME = useSelector(
    (state) => state.globalState?.siteSetting?.site_settings?.site_name
  );

  return (
    <Layout>
      <Helmet>
        <title>Corporate Code - {BRAND_NAME ?? ""}</title>
      </Helmet>

      <div className="p-4 bg-white rounded">
        <div className="flex items-center gap-1 py-2">
          <Button onClick={() => openModal({ type: "add" })} variant="primary">
            <div className="flex items-center gap-2">
              <FaPlus /> Add New
            </div>
          </Button>
        </div>

        {/* ---------- Filters ---------- */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 my-2">
          <InputField
            name="corporateCode"
            placeholder="Corporate Code"
            value={values.corporateCode}
            onChange={handleChange}
          />
          <InputField
            name="corporateName"
            placeholder="Corporate Name"
            value={values.corporateName}
            onChange={handleChange}
          />
          <button
            className="py-1 text-white rounded bg-emerald-600 hover:bg-emerald-800"
            onClick={() => setValues(searchInitialValues)}
          >
            Clear
          </button>
        </div>

        {/* ---------- Table ---------- */}
        <Table
          headers={[
            { label: "Corporate Name", key: "corporateName", sorting: true },
            { label: "Corporate Code", key: "corporateCode", sorting: true },
            { label: "Country", key: "country", sorting: false },
            { label: "State", key: "state", sorting: false },
            { label: "City", key: "city", sorting: false },
            { label: "Action", key: "action", sorting: false },
          ]}
          loading={loading}
          handleSort={handleSort}
          sort={sort}
          error={error}
          cols={6}
          renderRow={
            <tbody className="bg-white divide-y divide-gray-200">
              {data?.map((item) => (
                <tr key={item._id} className="hover:bg-gray-50">
                  <td className="px-3 py-3 font-medium">
                    {item.corporateName}
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-600">
                    {item.corporateCode}
                  </td>
                  <td className="px-3 py-3 text-sm">
                    {item.countryId?.name || "-"}
                  </td>
                  <td className="px-3 py-3 text-sm">
                    {item.stateId?.name || "-"}
                  </td>
                  <td className="px-3 py-3 text-sm">
                    {item.cityId?.name || "-"}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex justify-center gap-3">
                      <button
                        onClick={() =>
                          openModal({
                            type: "edit",
                            data: { state: item },
                          })
                        }
                        className="text-primary"
                        title="Edit"
                      >
                        <FaEdit size={16} />
                      </button>
                      <button
                        onClick={() =>
                          openModal({
                            type: "delete",
                            data: { state: item },
                          })
                        }
                        className="text-red-500"
                        title="Delete"
                      >
                        <FaTrashAlt size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          }
        />

        {/* ---------- Pagination ---------- */}
        {totalPages > 1 && (
          <Pagination
            pagination={{
              currentPage,
              totalPages,
              totalItems,
              perPage: limit,
            }}
            handlePageChange={setPage}
            loading={loading}
          />
        )}
      </div>

      {/* ---------- Modal ---------- */}
      <Modal isOpen={isOpen} onClose={closeModal} size="lg">
        {actionType?.type === "add" && (
          <CreateCorporateCode
            closeModal={closeModal}
            callback={fetchData}
          />
        )}
        {actionType?.type === "edit" && (
          <EditCorporateCode
            data={actionType?.data?.state}
            closeModal={closeModal}
            callback={fetchData}
          />
        )}
        {actionType?.type === "delete" && (
          <DeleteCorporateCode
            data={actionType?.data?.state}
            closeModal={closeModal}
            callback={fetchData}
          />
        )}
      </Modal>
    </Layout>
  );
};

export default CorporateCode;
